import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useScopedAuth } from "@/components/CurrentShopProvider";
import { repository } from "@/infrastructure/repositories";
import type { Condition } from "@/domain/repositoryInterface/Repository";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export type MenuCategoryRow = Tables<"menu_categories">;
export type MenuRow = Tables<"menus">;
export type ResourceTypeRow = Tables<"resource_types">;
export type ResourceRow = Tables<"resources">;
export type TagRow = Tables<"tags">;

const menuCategoryRepo = repository("menu_categories");
const menuRepo = repository("menus");
const resourceTypeRepo = repository("resource_types");
const resourceRepo = repository("resources");
const resourceMenuRepo = repository("resource_menus");
const tagRepo = repository("tags");
const staffSkillRepo = repository("staff_skills");

// ===== Menu Categories =====
export function useMenuCategories() {
  const { authUser } = useScopedAuth();
  return useQuery<MenuCategoryRow[]>({
    queryKey: ["menu_categories", authUser?.shop_id],
    queryFn: async () => {
      const where: Condition[] = [{ column: "deleted_at", op: "isNull" }];
      if (authUser?.shop_id) where.push({ column: "shop_id", op: "eq", value: authUser.shop_id });
      const { data } = await menuCategoryRepo.findMany({
        where,
        orderBy: [{ column: "sort_order" }],
      });
      return data;
    },
  });
}

export function useCreateMenuCategory() {
  const qc = useQueryClient();
  const { authUser } = useScopedAuth();
  return useMutation({
    mutationFn: async (v: { category_name: string; sort_order?: number | null }) => {
      await menuCategoryRepo.create({
        category_name: v.category_name,
        sort_order: v.sort_order ?? null,
        shop_id: authUser?.shop_id ?? null,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["menu_categories"] }),
  });
}

export function useUpdateMenuCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...v }: { id: string; category_name: string; sort_order?: number | null }) => {
      await menuCategoryRepo.update({
        id,
        patch: { category_name: v.category_name, sort_order: v.sort_order ?? null },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["menu_categories"] }),
  });
}

export function useSoftDeleteMenuCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await menuCategoryRepo.update({ id, patch: { deleted_at: new Date().toISOString() } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["menu_categories"] }),
  });
}

// ===== Menus =====
export function useMenus() {
  const { authUser } = useScopedAuth();
  return useQuery<MenuRow[]>({
    queryKey: ["menus", authUser?.shop_id],
    queryFn: async () => {
      const where: Condition[] = [{ column: "deleted_at", op: "isNull" }];
      if (authUser?.shop_id) where.push({ column: "shop_id", op: "eq", value: authUser.shop_id });
      const { data } = await menuRepo.findMany({
        where,
        orderBy: [{ column: "sort_order" }, { column: "id" }],
      });
      return data;
    },
  });
}

export function useCreateMenu() {
  const qc = useQueryClient();
  const { authUser } = useScopedAuth();
  return useMutation({
    mutationFn: async (v: { menu_name: string; duration: number; price?: number | null; is_available?: boolean; menu_category_id?: string | null; sort_order?: number | null }) => {
      await menuRepo.create({
        menu_name: v.menu_name, duration: v.duration, price: v.price ?? null,
        is_available: v.is_available ?? true, menu_category_id: v.menu_category_id || null,
        sort_order: v.sort_order ?? null, shop_id: authUser?.shop_id ?? "",
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["menus"] }),
  });
}

export function useUpdateMenu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...v }: { id: string; menu_name: string; duration: number; price?: number | null; is_available?: boolean; menu_category_id?: string | null; sort_order?: number | null; image_url?: string | null }) => {
      await menuRepo.update({
        id,
        patch: {
          menu_name: v.menu_name, duration: v.duration, price: v.price ?? null,
          is_available: v.is_available ?? true, menu_category_id: v.menu_category_id || null,
          sort_order: v.sort_order ?? null, image_url: v.image_url ?? null,
        },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["menus"] }),
  });
}

export function useToggleMenuAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_available }: { id: string; is_available: boolean }) => {
      await menuRepo.update({ id, patch: { is_available } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["menus"] }),
  });
}

export function useSoftDeleteMenu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await menuRepo.update({ id, patch: { deleted_at: new Date().toISOString() } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["menus"] }),
  });
}

// ===== Resource Types =====
export function useResourceTypes() {
  return useQuery<ResourceTypeRow[]>({
    queryKey: ["resource_types"],
    queryFn: async () => {
      const { data } = await resourceTypeRepo.findMany({
        where: [{ column: "deleted_at", op: "isNull" }],
        orderBy: [{ column: "sort_order" }],
      });
      return data;
    },
  });
}

export function useCreateResourceType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { type_name: string; sort_order?: number | null }) => {
      await resourceTypeRepo.create({
        type_name: v.type_name, sort_order: v.sort_order ?? null,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resource_types"] }),
  });
}

export function useUpdateResourceType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...v }: { id: string; type_name: string; sort_order?: number | null }) => {
      await resourceTypeRepo.update({
        id,
        patch: { type_name: v.type_name, sort_order: v.sort_order ?? null },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resource_types"] }),
  });
}

export function useSoftDeleteResourceType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await resourceTypeRepo.update({ id, patch: { deleted_at: new Date().toISOString() } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resource_types"] }),
  });
}

// ===== Resource ↔ Menu Mapping =====
export interface ResourceMenuJoin {
  id: string;
  resource_id: string;
  menu_id: string;
  menu_name?: string;
  duration?: number;
}

interface ResourceMenuJoinRow {
  id: string;
  resource_id: string;
  menu_id: string;
  menus: { menu_name: string; duration: number } | null;
}

export function useResourceMenus(resourceId: string | null) {
  return useQuery<ResourceMenuJoin[]>({
    queryKey: ["resource_menus", resourceId],
    enabled: !!resourceId,
    queryFn: async () => {
      const { data } = await resourceMenuRepo.findMany<ResourceMenuJoinRow>({
        select: "id, resource_id, menu_id, menus(menu_name, duration)",
        where: [{ column: "resource_id", op: "eq", value: resourceId! }],
      });
      return (data ?? []).map((row) => ({
        id: row.id,
        resource_id: row.resource_id,
        menu_id: row.menu_id,
        menu_name: row.menus?.menu_name,
        duration: row.menus?.duration,
      }));
    },
  });
}

export function useAddResourceMenu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { resource_id: string; menu_id: string }) => {
      await resourceMenuRepo.create({ resource_id: params.resource_id, menu_id: params.menu_id });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resource_menus"] }),
  });
}

export function useRemoveResourceMenu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await resourceMenuRepo.delete([id]);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resource_menus"] }),
  });
}

// ===== Resources =====
export function useResources() {
  const { authUser } = useScopedAuth();
  return useQuery<ResourceRow[]>({
    queryKey: ["resources", authUser?.shop_id],
    queryFn: async () => {
      const where: Condition[] = [{ column: "deleted_at", op: "isNull" }];
      if (authUser?.shop_id) where.push({ column: "shop_id", op: "eq", value: authUser.shop_id });
      const { data } = await resourceRepo.findMany({
        where,
        orderBy: [{ column: "sort_order" }],
      });
      return data;
    },
  });
}

export function useCreateResource() {
  const qc = useQueryClient();
  const { authUser } = useScopedAuth();
  return useMutation({
    mutationFn: async (v: { resource_name: string; resource_type_id?: string | null; sort_order?: number | null }) => {
      await resourceRepo.create({
        resource_name: v.resource_name, resource_type_id: v.resource_type_id || null,
        sort_order: v.sort_order ?? null, shop_id: authUser?.shop_id ?? "",
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resources"] }),
  });
}

export function useUpdateResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...v }: { id: string; resource_name: string; resource_type_id?: string | null; sort_order?: number | null }) => {
      await resourceRepo.update({
        id,
        patch: {
          resource_name: v.resource_name, resource_type_id: v.resource_type_id || null,
          sort_order: v.sort_order ?? null,
        },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resources"] }),
  });
}

export function useSoftDeleteResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await resourceRepo.update({ id, patch: { deleted_at: new Date().toISOString() } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resources"] }),
  });
}

export function useSaveResourcesInline() {
  const qc = useQueryClient();
  const { authUser } = useScopedAuth();
  return useMutation({
    mutationFn: async (
      rows: Array<{ id: string; resource_name: string; resource_type_id?: string | null; sort_order?: number | null; isNew?: boolean; markedForDelete?: boolean }>
    ) => {
      for (const row of rows) {
        if (row.markedForDelete && !row.isNew) {
          await resourceRepo.update({ id: row.id, patch: { deleted_at: new Date().toISOString() } });
        } else if (!row.markedForDelete) {
          const rec = {
            resource_name: row.resource_name,
            resource_type_id: row.resource_type_id || null,
            sort_order: row.sort_order ?? null,
            capacity: (row as any).capacity ?? 1,
            allocation_order: (row as any).allocation_order ?? null,
          };
          if (row.isNew) {
            await resourceRepo.create({ ...rec, shop_id: authUser?.shop_id ?? "" } as unknown as TablesInsert<"resources">);
          } else {
            await resourceRepo.update({ id: row.id, patch: rec as unknown as TablesUpdate<"resources"> });
          }
        }
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resources"] }),
  });
}

// ===== Tags =====
export function useTags() {
  const { authUser } = useScopedAuth();
  return useQuery<TagRow[]>({
    queryKey: ["tags", authUser?.shop_id],
    queryFn: async () => {
      const where: Condition[] = [{ column: "deleted_at", op: "isNull" }];
      if (authUser?.shop_id) where.push({ column: "shop_id", op: "eq", value: authUser.shop_id });
      const { data } = await tagRepo.findMany({
        where,
        orderBy: [{ column: "sort_order" }],
      });
      return data;
    },
  });
}

export function useCreateTag() {
  const qc = useQueryClient();
  const { authUser } = useScopedAuth();
  return useMutation({
    mutationFn: async (v: { tag_name: string; color?: string | null; tag_text_color?: string | null; booking_text_color?: string | null; booking_bg_color?: string | null }) => {
      await tagRepo.create({
        tag_name: v.tag_name, color: v.color || null,
        tag_text_color: v.tag_text_color || null, booking_text_color: v.booking_text_color || null, booking_bg_color: v.booking_bg_color || null,
        shop_id: authUser?.shop_id ?? "",
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tags"] }),
  });
}

export function useUpdateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...v }: { id: string; tag_name: string; color?: string | null; tag_text_color?: string | null; booking_text_color?: string | null; booking_bg_color?: string | null }) => {
      await tagRepo.update({
        id,
        patch: {
          tag_name: v.tag_name, color: v.color || null,
          tag_text_color: v.tag_text_color || null, booking_text_color: v.booking_text_color || null, booking_bg_color: v.booking_bg_color || null,
        },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tags"] }),
  });
}

export function useSoftDeleteTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await tagRepo.update({ id, patch: { deleted_at: new Date().toISOString() } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tags"] }),
  });
}

// ===== Staff Skills (staff ↔ menu mapping) =====
export type StaffSkillRow = Tables<"staff_skills">;

export interface StaffSkillJoin {
  id: string;
  staff_id: string;
  menu_id: string;
  menu_name?: string;
  duration?: number;
}

interface StaffSkillJoinRow {
  id: string;
  staff_id: string;
  menu_id: string;
  menus: { menu_name: string; duration: number } | null;
}

export function useStaffSkills(staffId: string | null) {
  return useQuery<StaffSkillJoin[]>({
    queryKey: ["staff_skills", staffId],
    enabled: !!staffId,
    queryFn: async () => {
      const { data } = await staffSkillRepo.findMany<StaffSkillJoinRow>({
        select: "id, staff_id, menu_id, menus(menu_name, duration)",
        where: [{ column: "staff_id", op: "eq", value: staffId! }],
      });
      return (data ?? []).map((row) => ({
        id: row.id,
        staff_id: row.staff_id,
        menu_id: row.menu_id,
        menu_name: row.menus?.menu_name,
        duration: row.menus?.duration,
      }));
    },
  });
}

export function useAddStaffSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { staff_id: string; menu_id: string }) => {
      await staffSkillRepo.create({ staff_id: params.staff_id, menu_id: params.menu_id });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff_skills"] }),
  });
}

export function useRemoveStaffSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await staffSkillRepo.delete([id]);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff_skills"] }),
  });
}
